/**
 * @lines98/client/services/game-start — POST /game/start wrapper (extra-modes, ADR-0013).
 *
 * Registers a non-daily server-scored game session and returns its server-issued
 * seed + session_id. Called from the mode-selector (§5.1) on card-click, before
 * `startAllTimeGame` mounts the board. The seed is server-authoritative (the
 * client never picks it — anti-cheat §3.3); the session_id is later sent back in
 * `POST /finish-game` with `context = { kind:'all_time', session_id }`.
 *
 * Cross-references:
 *   - ADR-0013 §1.2 (POST /game/start contract).
 *   - extra-modes-alltime.md §2.2 / §5.1 (mode-selector entry).
 *   - packages/server/src/routes/game-start.ts (server contract).
 */

import type { GameMode } from '@lines98/shared/types/game';

import { apiClient, ApiClientError } from './api-client.js';

/** Server response of `POST /game/start`. */
interface StartGameResponseBody {
  session_id: string;
  seed: number;
  mode: GameMode;
}

/** Discriminated result for the mode-selector to branch on. */
export type StartGameResult =
  | { readonly status: 'ok'; readonly sessionId: string; readonly seed: number; readonly mode: GameMode }
  | { readonly status: 'rate_limited' }
  | { readonly status: 'network' };

/**
 * Start a non-daily game session for `mode`. Single attempt (no retry: a duplicate
 * start would just allocate another session — harmless but wasteful; the caller
 * surfaces a retry CTA on failure).
 */
export async function startGame(mode: GameMode): Promise<StartGameResult> {
  let response;
  try {
    response = await apiClient.post<StartGameResponseBody>('/game/start', { mode });
  } catch (err) {
    if (err instanceof ApiClientError) return { status: 'network' };
    return { status: 'network' };
  }

  if (response.ok) {
    const body = response.body;
    return { status: 'ok', sessionId: body.session_id, seed: body.seed, mode: body.mode };
  }

  // Typed failure. Rate-limited starts are recoverable (the player just retries);
  // anything else is surfaced as a generic network/retry case.
  const reason = (response.body as { reason?: string } | null)?.reason;
  if (response.status === 429 || reason === 'rate_limited') {
    return { status: 'rate_limited' };
  }
  return { status: 'network' };
}
